import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  alpha,
} from '@mui/material';
import { X, ArrowRight, Trash2 } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { API_URL } from '../globals';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const TestCaseDialog = ({ open, onClose, page, onDeleted }) => {
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingCaseId, setDeletingCaseId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTestCases = async () => {
      if (!page?.page_id) return;
      
      setLoading(true);
      setError(null);
      try {
        const session = await supabase.auth.getSession();
        if (!session?.data?.session?.access_token) {
          throw new Error("Not authenticated");
        }
        const authToken = session.data.session.access_token;
        const headers = {
          Authorization: `Bearer ${authToken}`,
        };

        const response = await axios.get(`${API_URL}/test/test_cases?page_id=${page.page_id}`, { headers });
        
        if (response.data) {
          setTestCases(response.data);
        } else {
          setTestCases([]);
        }
      } catch (err) {
        console.error("Error fetching test cases:", err);
        setError(err.message || "Failed to fetch test cases");
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchTestCases();
    }
  }, [open, page]);

  const handleTestCaseClick = (testCase) => {
    onClose();
    navigate(`/test/case/${testCase.case_id}`, { state: { testRun: testCase } });
  };

  const handleDeleteTestCase = async (event, caseId) => {
    event.stopPropagation();
    if (!caseId) return;

    const confirmed = window.confirm('Are you sure you want to delete this test case? This action cannot be undone.');
    if (!confirmed) return;

    try {
      setDeletingCaseId(caseId);

      const session = await supabase.auth.getSession();
      if (!session?.data?.session?.access_token) {
        throw new Error('Not authenticated');
      }
      const authToken = session.data.session.access_token;

      await axios.delete(`${API_URL}/test/delete_test_case`, {
        headers: { Authorization: `Bearer ${authToken}` },
        data: { case_id: caseId },
      });

      setTestCases((prev) => prev.filter((tc) => tc.case_id !== caseId));
      if (typeof onDeleted === 'function') {
        onDeleted();
      }
    } catch (err) {
      console.error('Error deleting test case:', err);
      alert(err?.response?.data?.error || err.message || 'Failed to delete test case');
    } finally {
      setDeletingCaseId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1A1A1A',
          backgroundImage: 'none',
          borderRadius: '12px',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        pb: 2
      }}>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 500 }}>
          Test Cases - {page?.name || 'Unnamed Page'}
        </Typography>
        <IconButton 
          onClick={onClose}
          size="small"
          sx={{ 
            color: '#666',
            '&:hover': { 
              backgroundColor: alpha('#FFFFFF', 0.1) 
            }
          }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '200px' 
          }}>
            <CircularProgress size={24} sx={{ color: '#fff' }} />
          </Box>
        ) : error ? (
          <Box sx={{ 
            p: 3, 
            textAlign: 'center',
            color: '#f44336'
          }}>
            {error}
          </Box>
        ) : testCases.length === 0 ? (
          <Box sx={{ 
            p: 3, 
            textAlign: 'center',
            color: '#888'
          }}>
            No test cases found for this page.
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {testCases.map((testCase) => (
              <ListItem
                key={testCase.case_id}
                onClick={() => handleTestCaseClick(testCase)}
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                  py: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:last-child': {
                    borderBottom: 'none'
                  },
                  '&:hover': {
                    backgroundColor: alpha('#FFFFFF', 0.05),
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 500, flex: 1 }}>
                    {testCase.name || 'Untitled Test Case'}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteTestCase(e, testCase.case_id)}
                    disabled={deletingCaseId === testCase.case_id}
                    sx={{
                      color: deletingCaseId === testCase.case_id ? '#555' : '#f44336',
                      '&:hover': {
                        backgroundColor: alpha('#f44336', 0.1)
                      }
                    }}
                    title="Delete test case"
                  >
                    <Trash2 size={18} />
                  </IconButton>
                  <ArrowRight size={20} color="#666" />
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TestCaseDialog; 